import { useCallback, useMemo, useRef, useState } from "react";
import { INiche } from "@/common/services/types";
import { useNichesQuery } from "@/common/queries/queries";

const PAGE_SIZE = 30;

interface IProps {
  initialOrder?: string;
  count?: number;
  tag?: string;
}

interface IUseFilterNichesQuery {
  niches: INiche[];
  isLoading: boolean;
  hasMore: boolean;
  handleFetchMore: () => void;
  order: string;
  sort: string;
  handleOrderChange: (order: string, sort: string) => void;
}

export const useFilterNichesQuery = (props?: IProps): IUseFilterNichesQuery => {
  const { initialOrder = "subscribers", count = PAGE_SIZE, tag } = props || {};

  const [order, setOrder] = useState<string>(initialOrder);
  const [sort, setSort] = useState<string>("asc");
  const [page, setPage] = useState<number>(1);
  const prevNiches = useRef<INiche[]>([]);

  const { isSuccess, data, isLoading } = useNichesQuery({
    order,
    sort,
    page,
    count,
    tag,
  });

  const niches = useMemo(() => {
    if (isSuccess) {
      const items = data.niches.filter(niche => {
        return niche.previews.length >= 4;
      });

      prevNiches.current = [...prevNiches.current, ...items];
    }

    return prevNiches.current;
  }, [isSuccess, data]);

  const handleOrderChange = useCallback((order: string, sort: string) => {
    setOrder(order);
    setSort(sort);
    setPage(1);
    prevNiches.current = [];
  }, []);

  const hasMore = useMemo(() => {
    return isSuccess ? data.total > page * count : false;
  }, [page, data]);

  const handleFetchMore = useCallback(() => {
    setPage(prev => prev + 1);
  }, []);

  return {
    niches,
    isLoading,
    hasMore,
    handleFetchMore,
    order,
    sort,
    handleOrderChange,
  };
};
